import classNames from "classnames";
import router from "next/router";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";

import { useBreakpoint } from "~/contexts/breakpoint";
import useScript from "~/hooks/use-script";
import { Coords } from "~/shared/coords.class";
import { AbstractShop } from "~/shared/shipping/abstract-shop.interface";
import { WooosmapGeometry } from "~/utils/woosmap-utils";

import mapMarkerBlack from "../../../public/backgrounds/map-marker-black.svg";
import mapMarkerWhite from "../../../public/backgrounds/map-marker-white.svg";
import { calculateDistance, getOpeningHours } from "./stores-modal";
import styles from "./stores-modal.module.scss";

const defaultMarkerConfig = {
  url: mapMarkerWhite.src,
  scaledSize: {
    height: 38,
    width: 26,
  },
};

const selectedMarkerConfig = {
  url: mapMarkerBlack.src,
  scaledSize: {
    height: 38,
    width: 26,
  },
};

type markerExtended = {
  id: number | string;
  marker: woosmap.map.Marker;
  selected: boolean;
};

type Props = {
  stores?: AbstractShop[] | void;
  selectedStoreID: number | string | null;
  location?: Coords;
  onSelectStore: (id: number | string) => void;
  inPickUp?: boolean;
  inModal?: boolean;
  inPDP?: boolean;
  flyTo?: WooosmapGeometry;
};

export function StoresMap(props: Props) {
  const t = useTranslations();
  const breakpoint = useBreakpoint();

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [markers, setMarkers] = useState<markerExtended[]>([]);
  const [map, setMap] = useState<woosmap.map.Map>();

  const woosmapLoaded = useScript(
    `${process.env.NEXT_PUBLIC_WOOSMAP_MAP_API_URL}?key=${process.env.NEXT_PUBLIC_WOOSMAP_API_KEY}`
  );

  //set active store
  const activeStore = useMemo(() => {
    return props.stores?.find((store: any) => store.ID == props.selectedStoreID);
  }, [props.selectedStoreID, props.stores]);

  //Check if woosmap exist and init
  useEffect(() => {
    if (woosmapLoaded) {
      let initInterval = setInterval(() => {
        if (window.woosmap && window.woosmap.map) {
          clearInterval(initInterval);
          setMap(init());
        }
      }, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woosmapLoaded]);

  useEffect(() => {
    if (!!map && !!props.flyTo) {
      if (props.flyTo.viewport) {
        const bounds = new woosmap.map.LatLngBounds(props.flyTo.viewport.northeast, props.flyTo.viewport.southwest);
        // @ts-ignore the animation parameter is not in the types
        map.fitBounds(bounds, undefined, true);
      } else {
        map.flyTo({ center: props.flyTo.location, zoom: 12 });
      }
    }
  }, [map, props.flyTo]);

  const activeStoreRef = useRef<string>();
  // pan map on active store change
  useEffect(() => {
    if (markers.length > 0 && !!map) {
      const previousSelected = markers.find((markerExtended) => markerExtended.selected);
      if (previousSelected) {
        previousSelected.selected = false;
        previousSelected.marker.setIcon(defaultMarkerConfig);
      }

      const activeMarker = markers.find((markerExtended) => markerExtended.id == activeStore?.ID);
      // TODO in future we will read the lat/lon only from store.Address
      if (
        activeMarker &&
        (activeStore?.Latitude || activeStore?.Address.Latitude) &&
        (activeStore?.Longitude || activeStore?.Address.Longitude)
      ) {
        activeMarker.selected = true;
        const latlng = {
          lat: (activeStore.Latitude || activeStore.Address.Latitude)!,
          lng: (activeStore.Longitude || activeStore.Address.Longitude)!,
        };
        activeMarker.marker.setIcon(selectedMarkerConfig);
        if (activeStoreRef.current != `${activeStore.ID}`) {
          activeStoreRef.current = `${activeStore.ID}`;
          map?.flyTo({ center: latlng, zoom: 12 });
        }
      }
    }
  }, [activeStore, map, markers]);

  const fitsOnFirstRender = useRef(false);
  useEffect(() => {
    if (map && props.stores) {
      // reset markers
      markers.map((markerE) => markerE.marker.setMap(null));
      let tempMarkers: markerExtended[] = [];
      // handle map fitBounds based on LatLngBounds
      const bounds = new woosmap.map.LatLngBounds();
      // then re-add them
      props.stores.forEach((store) => {
        let storeID = store.ID;
        const latlng = {
          lat: (store.Latitude || store.Address?.Latitude)!,
          lng: (store.Longitude || store.Address?.Longitude)!,
        };

        bounds.extend(latlng);

        if (latlng.lat && latlng.lng) {
          const marker = new woosmap.map.Marker({
            position: latlng,
            icon: defaultMarkerConfig,
            clickable: store.HasStock == undefined || store.HasStock || props.inPDP,
            opacity: store.HasStock == undefined || store.HasStock || props.inPDP ? 1 : 0.5,
          });

          marker.setMap(map);

          marker.addListener("click", () => {
            props.onSelectStore(storeID);
          });

          tempMarkers.push({ id: store.ID, marker: marker, selected: props.selectedStoreID === store.ID });
        }
      });
      // update the actual state
      setMarkers(tempMarkers);
      if (!activeStore && !props.flyTo && !fitsOnFirstRender.current) {
        fitsOnFirstRender.current = true;
        map.fitBounds(bounds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, props.stores]);

  const init = () => {
    return new woosmap.map.Map(mapContainerRef.current!);
  };

  const checkCoords = useMemo(
    () =>
      props.location?.lat != undefined &&
      props.location.lng != undefined &&
      (activeStore?.Latitude || activeStore?.Address.Latitude) != undefined &&
      (activeStore?.Longitude || activeStore?.Address.Longitude) != undefined,
    [props.location, activeStore]
  );

  return (
    <div className={classNames(styles.mapWrapper, { [styles.inModal]: props.inModal })}>
      {breakpoint === "mobile" && activeStore && (
        <div id={activeStore.ID.toString()} className={styles.pickup}>
          <div className={styles.pickupContainer}>
            <div className={styles.pickupRow}>
              <div className={styles.pickupName}>{activeStore.Name}</div>
              {(activeStore.DistanceInKm || checkCoords) && (
                <span className={styles.pickupDistance}>
                  {(activeStore.DistanceInKm
                    ? activeStore.DistanceInKm
                    : calculateDistance(
                        props.location!.lat!,
                        props.location!.lng!,
                        (activeStore.Latitude || activeStore.Address.Latitude)!,
                        (activeStore.Longitude || activeStore.Address.Longitude)!
                      )
                  )
                    .toFixed(1)
                    .replace(".", ",")}{" "}
                  km
                </span>
              )}
            </div>
            <div className={styles.pickupAddress}>
              {activeStore.Address?.Address1}
              {activeStore.Address?.City ? `, ${activeStore.Address.City}` : ""}
              {activeStore.Address?.CountryID ? `, ${activeStore.Address.CountryID}` : ""}
              {activeStore.Address?.PhoneNumber ? (
                <>
                  {` · `}
                  <a href={`tel:${activeStore.Address.PhoneNumber}`}>{activeStore.Address.PhoneNumber}</a>
                </>
              ) : (
                ""
              )}
            </div>
            {!props.inPickUp && (
              <div className={styles.stock}>
                <div
                  className={styles.stockIndicator}
                  style={{ backgroundColor: activeStore.HasStock ? "green" : "#CC0C0C" }}
                />
                <p className={styles.stockText}>
                  {activeStore.HasStock
                    ? t("generic.productAvailableAtThisLocation")
                    : t("generic.productUnavailableAtThisLocation")}
                </p>
              </div>
            )}
            {(activeStore.RegularOpeningHours || activeStore.OpeningHours) &&
              getOpeningHours(
                (activeStore.RegularOpeningHours || activeStore.OpeningHours) as EVA.Core.RegularOpeningHours[],
                router.locale,
                t("generic.storeOpeningHours")
              )}
            {activeStore.customOpening && (
              <div className={classNames(styles.hours, styles.hoursContent)}>{activeStore.customOpening}</div>
            )}
          </div>
        </div>
      )}
      <div id="map" ref={mapContainerRef} className={styles.map} />
    </div>
  );
}
